import { useState } from 'react';
import {
    Autocomplete,
    Box,
    Chip,
    IconButton,
    Link,
    Stack,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material';
import PropTypes from 'prop-types';

import ConfirmDialog from '../../dialogs/ConfirmDialog';
import Iconify from '../../../components/iconify';
import CodeEditor from '../../scraper/CodeEditor';
import JsonObjectEditor from './JsonObjectEditor';
import { scenarioEditorPath } from '../../scraper/scenarioPaths';
import {
    actionSource,
    findLinkedScenario,
    hasInlineCode,
    linkActionToScenario,
    setActionInlineCode
} from '../../../utils/scraperSettings';
import i18n from '../../../i18n';

// What a new link is written as. By name reads in the JSON — `"scenario":
// "bank-x-payin"` says what runs, `"scenario_id": 12` does not — and a scenario a
// gateway uses cannot be renamed from under it.
const DEFAULT_LINK_BY = 'name';

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * One xscraper action (payin_init, payout_make, …): inline `code`, or a saved
 * scraper scenario referenced by name or id.
 *
 * Every change rewrites the action's code / scenario / scenario_id keys, leaving
 * exactly one, and keeps its `input` — which holds the bank credentials — and
 * anything else as they were; the rewrites are utils/scraperSettings'. Switching
 * inline code to a scenario asks first, and switching back copies the scenario's
 * code in, so the settings go on running exactly what they ran.
 *
 * The settings form's Scenarios mode shows it with the code summarised; Form mode
 * with `showCode`, which edits the inline code and the action's input in Monaco.
 * In Form mode an action may not be configured yet (`action` undefined): picking a
 * source configures it, and `onRemove` takes it out again.
 */
export default function ScraperActionInput({
    actionKey,
    action,
    onChange,
    scenarios = [],
    store,
    disabled,
    showCode = false,
    editorPath = 'scraper-action',
    description,
    onRemove,
    inputError,
    inputHelperText
}) {
    // Switched to Scenario, its scenario not chosen yet. Nothing is written until it
    // is: an action with no source key at all would not even be an action any more.
    const [pending, setPending] = useState(false);
    // { scenario, linkBy } while the discard-code confirmation is up.
    const [confirmDiscard, setConfirmDiscard] = useState(null);
    // The scenario's code is being fetched to inline it.
    const [inlining, setInlining] = useState(false);

    const configured = isPlainObject(action);
    const source = actionSource(action);
    const shown = configured
        ? pending && source.source === 'code' ? 'scenario' : source.source
        : pending ? 'scenario' : null;
    const linked = findLinkedScenario(action, scenarios);
    const linkBy = source.source === 'scenario' ? source.linkBy : DEFAULT_LINK_BY;
    const rowDisabled = disabled || inlining;
    const missing = configured && source.source === 'scenario' && !linked;

    // The rewrites in utils/scraperSettings work on a whole settings object.
    const rewrite = (fn, ...args) => fn({ [actionKey]: configured ? action : {} }, actionKey, ...args)[actionKey];

    const link = (scenario, by) => {
        setPending(false);
        onChange(rewrite(linkActionToScenario, scenario, by));
    };

    const handleSourceChange = async (_event, next) => {
        if (!next) return;
        const current = configured ? source.source : null;

        if (next === 'scenario') {
            // Only shows the picker; the action changes when a scenario is chosen.
            if (current !== 'scenario') setPending(true);
            return;
        }

        setPending(false);
        if (current === 'code') return;
        if (!configured) {
            onChange({ code: '' });
            return;
        }
        // An ambiguous action already carries code of its own: keep that one.
        if (hasInlineCode(action)) {
            onChange(rewrite(setActionInlineCode, action.code));
            return;
        }

        // Otherwise a copy of the scenario's code — an empty script would fail the
        // gateway's next call. Fetched here because the list behind the picker is
        // the light one, without code.
        let code = '';
        if (linked) {
            setInlining(true);
            try {
                const result = await store.getState().server.publicScraperScenarios.getById(linked.id);
                if (result?.error === 'OK' && typeof result.data?.[0]?.code === 'string') {
                    code = result.data[0].code;
                }
            } catch (error) {
                console.error('Could not read the scenario to inline it:', error);
            } finally {
                setInlining(false);
            }
        }
        onChange(rewrite(setActionInlineCode, code));
    };

    const handleScenarioPick = (_event, scenario) => {
        if (!scenario) return;
        if (hasInlineCode(action)) {
            setConfirmDiscard({ scenario, linkBy });
            return;
        }
        link(scenario, linkBy);
    };

    const handleConfirmDiscard = () => {
        const confirmed = confirmDiscard;
        setConfirmDiscard(null);
        if (confirmed) link(confirmed.scenario, confirmed.linkBy);
    };

    const codeText = typeof source.code === 'string' ? source.code : '';
    const lines = codeText.trim() === '' ? 0 : codeText.split('\n').length;

    return (
        <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Typography variant="subtitle2" sx={{ fontFamily: 'monospace' }}>
                    {actionKey}
                </Typography>
                {source.ambiguous && (
                    <Tooltip title={i18n.t('scraperScenarios.actionAmbiguousHint')}>
                        <Chip
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={i18n.t('scraperScenarios.actionAmbiguous')}
                        />
                    </Tooltip>
                )}
                {!configured && showCode && (
                    <Typography variant="caption" color="text.secondary">
                        {i18n.t('gatewaySettings.notSet')}
                    </Typography>
                )}
                <Box sx={{ flex: 1 }} />
                {configured && onRemove && (
                    <Tooltip title={i18n.t('gatewaySettings.remove')}>
                        <span>
                            <IconButton size="small" disabled={disabled} aria-label={i18n.t('gatewaySettings.remove')} onClick={onRemove}>
                                <Iconify icon="eva:trash-2-outline" width={18} />
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
            </Stack>
            {description && (
                <Typography variant="caption" color="text.secondary" component="div" sx={{ mb: 1 }}>
                    {description}
                </Typography>
            )}

            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                <ToggleButtonGroup size="small" exclusive value={shown} disabled={rowDisabled} onChange={handleSourceChange}>
                    <ToggleButton value="code">{i18n.t('scraperScenarios.sourceInline')}</ToggleButton>
                    <ToggleButton value="scenario">{i18n.t('scraperScenarios.sourceScenario')}</ToggleButton>
                </ToggleButtonGroup>

                {shown === 'code' && !showCode && (
                    <Typography variant="body2" color="text.secondary">
                        {lines
                            ? i18n.t('scraperScenarios.inlineCodeLines', { lines })
                            : i18n.t('scraperScenarios.inlineCodeEmpty')}
                    </Typography>
                )}
                {shown === 'scenario' && (
                    <>
                        <Autocomplete
                            size="small"
                            sx={{ flex: '1 1 200px', minWidth: 180 }}
                            options={scenarios}
                            value={linked}
                            disabled={rowDisabled}
                            disableClearable={Boolean(linked)}
                            getOptionLabel={(option) => option.name || ''}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                            onChange={handleScenarioPick}
                            renderOption={({ key: _key, ...props }, option) => (
                                <li {...props} key={option.id}>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                                            {option.name}
                                        </Typography>
                                        {option.description && (
                                            <Typography variant="caption" color="text.secondary" noWrap component="div">
                                                {option.description}
                                            </Typography>
                                        )}
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={i18n.t('scraperScenarios.scenario')}
                                    error={missing}
                                    helperText={
                                        missing
                                            ? i18n.t('scraperScenarios.scenarioMissing', { ref: String(source.ref) })
                                            : scenarios.length === 0
                                                ? i18n.t('scraperScenarios.noScenarios')
                                                : undefined
                                    }
                                />
                            )}
                        />
                        <Tooltip title={i18n.t('scraperScenarios.linkByHint')}>
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={linkBy}
                                disabled={rowDisabled || !linked}
                                onChange={(_event, value) => value && linked && link(linked, value)}
                            >
                                <ToggleButton value="name">{i18n.t('scraperScenarios.linkByName')}</ToggleButton>
                                <ToggleButton value="id">{i18n.t('scraperScenarios.linkById')}</ToggleButton>
                            </ToggleButtonGroup>
                        </Tooltip>
                        {linked && (
                            // A new tab: leaving would close this dialog and
                            // lose whatever else is being edited in it.
                            <Link
                                href={scenarioEditorPath(linked.id)}
                                target="_blank"
                                rel="noopener noreferrer"
                                variant="body2"
                                sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                            >
                                {i18n.t('scraperScenarios.openInEditor')}
                                <Iconify icon="eva:external-link-outline" width={16} />
                            </Link>
                        )}
                    </>
                )}
            </Stack>

            {showCode && configured && shown === 'code' && (
                <CodeEditor
                    language="javascript"
                    path={`${editorPath}/code.js`}
                    value={codeText}
                    readOnly={rowDisabled}
                    ariaLabel={`${actionKey} code`}
                    onChange={(text) => onChange(rewrite(setActionInlineCode, text))}
                    sx={{ height: 220, mt: 1.5 }}
                />
            )}
            {showCode && configured && (
                <Box sx={{ mt: 1.5 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                        input
                    </Typography>
                    <JsonObjectEditor
                        value={action.input}
                        path={`${editorPath}/input.json`}
                        disabled={rowDisabled}
                        error={inputError}
                        helperText={inputHelperText || i18n.t('gatewaySettings.inputHelper')}
                        onChange={(input) => {
                            const next = { ...action };
                            if (input === undefined) delete next.input;
                            else next.input = input;
                            onChange(next);
                        }}
                    />
                </Box>
            )}

            {confirmDiscard && (
                // Mounted only while needed: ConfirmDialog reads its callbacks
                // once, in its constructor.
                <ConfirmDialog
                    dialogOpen
                    alertTitle={i18n.t('dialogs.confirmationTitle')}
                    alertText={i18n.t('scraperScenarios.discardInlineCode', {
                        action: actionKey,
                        scenario: confirmDiscard.scenario.name
                    })}
                    onConfirm={handleConfirmDiscard}
                    onCancel={() => setConfirmDiscard(null)}
                />
            )}
        </Box>
    );
}

ScraperActionInput.propTypes = {
    actionKey: PropTypes.string.isRequired,
    action: PropTypes.any,
    onChange: PropTypes.func.isRequired,
    scenarios: PropTypes.array,
    store: PropTypes.object,
    disabled: PropTypes.bool,
    showCode: PropTypes.bool,
    editorPath: PropTypes.string,
    description: PropTypes.node,
    onRemove: PropTypes.func,
    inputError: PropTypes.bool,
    inputHelperText: PropTypes.node
};
